// ATS (Applicant Tracking System) rule checker for the CV generator.
//
// 100% client-side: it reads the Typst SOURCE the user is editing and runs a
// set of heuristics that mirror how real ATS parsers read a CV. Nothing is
// uploaded — the same privacy guarantee as the compiler. The checks are
// deliberately conservative (favouring warnings over hard fails) because ATS
// behaviour varies between vendors; the goal is actionable guidance, not a
// verdict.

import { cvStrings } from "../cv-data";

export type AtsStatus = "pass" | "warn" | "fail" | "info";

export interface AtsCheck {
  id: string;
  label: string;
  status: AtsStatus;
  /** One-line result shown under the label. */
  detail: string;
  /** Optional fix suggestion, shown when status is warn/fail. */
  hint?: string;
  /** Weight in the score (0 = informational, doesn't affect the score). */
  weight: number;
}

export interface AtsCategory {
  id: string;
  title: string;
  checks: AtsCheck[];
}

export interface AtsReport {
  score: number; // 0..100
  grade: "A" | "B" | "C" | "D";
  categories: AtsCategory[];
  wordCount: number;
  /** Counts for the header summary. */
  counts: { pass: number; warn: number; fail: number };
}

// ── Plain-text extraction ────────────────────────────────────────────────
//
// Typst is content-mode by default; `[...]` enters content, and `#name(...)`
// enters code mode (where `[...]` re-enters content). We walk the source with
// that mode model so the extracted text keeps real prose (including the text
// inside grid/#link content blocks) while dropping code, set/show rules and
// markup punctuation. Good enough for word-count and keyword heuristics.

export function extractPlainText(src: string): string {
  // Strip comments first. The `[^:]` guard keeps `https://` URLs intact.
  let s = src.replace(/\/\*[\s\S]*?\*\//g, "\n");
  s = s.replace(/(^|[^:])\/\/[^\n]*/g, "$1");

  // Simple `#let name = "string"` bindings (used by the form-driven templates
  // to hold the personal data). Their `#name` references in content would
  // otherwise be dropped — resolve them so the name/email/phone/links still
  // reach the extracted text the ATS heuristics read.
  const vars = new Map<string, string>();
  for (const m of s.matchAll(/#let\s+([A-Za-z_]\w*)\s*=\s*"((?:[^"\\]|\\.)*)"/g)) {
    vars.set(m[1], m[2].replace(/\\(["\\])/g, "$1"));
  }

  const out: string[] = [];
  const n = s.length;

  const isIdent = (c: string) => /[A-Za-z0-9_.]/.test(c);
  const atLineStart = (i: number) => {
    let k = i - 1;
    while (k >= 0 && (s[k] === " " || s[k] === "\t")) k--;
    return k < 0 || s[k] === "\n";
  };

  // Skip a set/show/let/import statement to the newline at bracket depth 0.
  function skipStatement(i: number): number {
    let depth = 0;
    for (; i < n; i++) {
      const c = s[i];
      if (c === "(" || c === "[" || c === "{") depth++;
      else if (c === ")" || c === "]" || c === "}") depth = Math.max(0, depth - 1);
      else if (c === "\n" && depth === 0) return i;
    }
    return i;
  }

  // Skip the code header of a control statement (`#for … [`, `#if … [`) up to
  // its content block, so the condition/loop code isn't emitted as prose. The
  // caller then processes the `[…]` body normally.
  function skipToContent(i: number): number {
    let depth = 0;
    for (; i < n; i++) {
      const c = s[i];
      if (c === '"') {
        i++;
        while (i < n && s[i] !== '"') {
          if (s[i] === "\\") i++;
          i++;
        }
        continue;
      }
      if (c === "(") depth++;
      else if (c === ")") depth = Math.max(0, depth - 1);
      else if (c === "[" && depth === 0) return i; // body starts here
      else if (c === "\n" && depth === 0) return i; // no block — bail
    }
    return i;
  }

  // Content mode: emit prose, recurse into nested blocks/calls. `stop` is the
  // closing char that ends this block (']' or '\0' for top level).
  function content(i: number, stop: string): number {
    while (i < n) {
      const c = s[i];
      if (stop && c === stop) return i + 1;
      if (c === "#") {
        let j = i + 1;
        while (j < n && isIdent(s[j])) j++;
        const name = s.slice(i + 1, j);
        if (["set", "show", "let", "import", "include"].includes(name)) {
          i = skipStatement(j);
          continue;
        }
        if (["for", "if", "while", "else"].includes(name)) {
          i = skipToContent(j);
          continue;
        }
        let k = j;
        while (k < n && (s[k] === " " || s[k] === "\t")) k++;
        if (s[k] === "(") {
          i = code(k + 1);
          continue;
        }
        if (s[k] === "[") {
          out.push(" ");
          i = content(k + 1, "]");
          continue;
        }
        // Bare #variable: emit the value if it's a known #let string, else drop.
        if (vars.has(name)) out.push(` ${vars.get(name)!} `);
        i = j;
        continue;
      }
      if (c === "[") {
        out.push(" ");
        i = content(i + 1, "]");
        continue;
      }
      if (c === "*" || c === "_" || c === "`" || c === "$") {
        i++;
        continue;
      }
      if (c === "\\" && i + 1 < n) {
        out.push(s[i + 1]);
        i += 2;
        continue;
      }
      if ((c === "=" || c === "-" || c === "+") && atLineStart(i)) {
        out.push(" ");
        i++;
        continue;
      }
      out.push(c);
      i++;
    }
    return i;
  }

  // Code mode: drop everything except nested content blocks and strings we
  // don't want. Recurse into `[...]` (content) and `(...)` (code). Ends at the
  // matching ')'.
  function code(i: number): number {
    while (i < n) {
      const c = s[i];
      if (c === ")") return i + 1;
      if (c === "(") {
        i = code(i + 1);
        continue;
      }
      if (c === "[") {
        out.push(" ");
        i = content(i + 1, "]");
        continue;
      }
      if (c === '"') {
        // skip string literal (usually URLs, font names, sizes — not prose)
        i++;
        while (i < n && s[i] !== '"') {
          if (s[i] === "\\") i++;
          i++;
        }
        i++;
        continue;
      }
      i++;
    }
    return i;
  }

  content(0, "");

  // The structured `#let cv = (…)` block (templates render it via #for loops,
  // which the walker drops) holds the real prose — append every string in it.
  for (const v of cvStrings(src)) out.push(` ${v} `);

  return out
    .join("")
    .replace(/\\([@#&%])/g, "$1")
    .replace(/[ \t]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ── Helpers ──────────────────────────────────────────────────────────────

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

function wordsOf(text: string): string[] {
  return text.split(/\s+/).filter(Boolean);
}

// Section heading synonyms (normalized, accent-free). Any heading whose text
// contains one of these tokens counts as that canonical section.
const SECTION_SYNONYMS: Record<string, string[]> = {
  experiencia: ["experiencia", "experience", "trayectoria", "empleo", "work", "laboral", "profesional"],
  formacion: ["formacion", "educacion", "education", "estudios", "academic"],
  habilidades: ["habilidades", "skills", "competencias", "aptitudes", "tecnologias", "stack", "conocimientos"],
};

// Action verbs that make good bullet openers (ES conjugated 1st person + EN).
const ACTION_VERBS = new Set(
  [
    // ES
    "lidere", "dirigi", "coordine", "gestione", "desarrolle", "implemente",
    "diseñe", "disene", "cree", "construi", "automatice", "optimice", "mejore",
    "reduje", "aumente", "incremente", "analice", "redacte", "migre",
    "configure", "desplegue", "resolvi", "entregue", "colabore", "forme",
    "mantuve", "impulse", "lance", "planifique", "ejecute", "supervise",
    "audite", "documente", "integre", "escale", "prototipe", "negocie",
    // EN
    "led", "directed", "coordinated", "managed", "developed", "implemented",
    "designed", "created", "built", "automated", "optimized", "improved",
    "reduced", "increased", "analyzed", "wrote", "migrated", "configured",
    "deployed", "resolved", "delivered", "collaborated", "maintained",
    "launched", "spearheaded", "engineered", "architected", "streamlined",
    "audited", "documented", "integrated", "scaled", "negotiated", "mentored",
  ].map(normalize),
);

// Fonts that ATS parsers / PDF text extraction handle cleanly.
const SAFE_FONTS = new Set(
  [
    "libertinus serif", "new computer modern", "dejavu sans mono", "dejavu sans",
    "arial", "helvetica", "helvetica neue", "times", "times new roman", "calibri",
    "georgia", "cambria", "garamond", "verdana", "tahoma", "roboto", "open sans",
    "lato", "source sans pro", "source sans 3", "carlito", "liberation serif",
    "liberation sans",
  ].map((f) => f.toLowerCase()),
);

function extractHeadings(src: string): string[] {
  const out: string[] = [];
  for (const line of src.split("\n")) {
    const m = line.match(/^\s*(=+)\s+(.+?)\s*$/);
    if (m) out.push(m[2].replace(/[*_`#]/g, "").trim());
  }
  return out;
}

// ── Main analysis ────────────────────────────────────────────────────────

export function analyzeAts(src: string): AtsReport {
  const text = extractPlainText(src);
  const words = wordsOf(text);
  const wordCount = words.length;
  const nText = normalize(text);
  const headings = extractHeadings(src);
  const nHeadings = headings.map(normalize);

  const checks: AtsCheck[] = [];
  const push = (c: AtsCheck) => checks.push(c);
  const catOf: Record<string, string> = {};

  // ── Contacto ──
  const emailMatch = text.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/);
  push({
    id: "email",
    label: "Correo electrónico",
    weight: 3,
    status: emailMatch ? "pass" : "fail",
    detail: emailMatch ? `Detectado: ${emailMatch[0]}` : "No se encontró ninguna dirección de correo.",
    hint: emailMatch ? undefined : "Añade tu email en la cabecera, como texto (no dentro de una imagen).",
  });
  catOf["email"] = "contacto";

  // Phone: 8+ digits allowing separators. Run on extracted text so #set sizes
  // (10.5pt) don't false-positive.
  const phoneMatch = text.match(/(\+?\d[\d\s().-]{7,}\d)/);
  const phoneOk = !!phoneMatch && (phoneMatch[0].replace(/\D/g, "").length >= 8);
  push({
    id: "phone",
    label: "Teléfono",
    weight: 2,
    status: phoneOk ? "pass" : "warn",
    detail: phoneOk ? `Detectado: ${phoneMatch![0].trim()}` : "No se encontró un teléfono.",
    hint: phoneOk ? undefined : "Incluye un número de contacto en formato internacional (+34 …).",
  });
  catOf["phone"] = "contacto";

  const linkUrls = [...src.matchAll(/#link\(\s*"([^"]+)"/g)].map((m) => m[1]);
  const hasLinkedin = linkUrls.some((u) => /linkedin\.com/i.test(u)) || /linkedin\.com/i.test(nText);
  const hasProfile =
    hasLinkedin ||
    linkUrls.some((u) => /github\.com|gitlab\.com|https?:\/\//i.test(u)) ||
    /github\.com|https?:\/\//i.test(text);
  push({
    id: "profile",
    label: "Perfil online (LinkedIn / web)",
    weight: 1,
    status: hasProfile ? "pass" : "warn",
    detail: hasProfile
      ? hasLinkedin
        ? "LinkedIn detectado."
        : "Enlace de perfil detectado."
      : "No se detectó LinkedIn ni web personal.",
    hint: hasProfile ? undefined : "Añade tu LinkedIn — es de los primeros campos que buscan los reclutadores.",
  });
  catOf["profile"] = "contacto";

  // ── Estructura / secciones ──
  const hasSection = (key: string) =>
    nHeadings.some((h) => SECTION_SYNONYMS[key].some((syn) => h.includes(syn)));
  const hasExperience = hasSection("experiencia");
  const hasEducation = hasSection("formacion");
  const hasSkills = hasSection("habilidades");

  push({
    id: "sections",
    label: "Secciones canónicas",
    weight: 3,
    status: hasExperience && hasEducation ? "pass" : hasExperience || hasEducation ? "warn" : "fail",
    detail: `Detectadas: ${[
      hasExperience && "Experiencia",
      hasEducation && "Formación",
      hasSkills && "Habilidades",
    ]
      .filter(Boolean)
      .join(", ") || "ninguna reconocida"}.`,
    hint:
      hasExperience && hasEducation
        ? undefined
        : "Usa encabezados estándar (Experiencia, Formación, Habilidades). Los ATS mapean el texto por sección.",
  });
  catOf["sections"] = "estructura";

  push({
    id: "skills",
    label: "Sección de habilidades / keywords",
    weight: 2,
    status: hasSkills ? "pass" : "warn",
    detail: hasSkills ? "Presente." : "No se detectó una sección de habilidades.",
    hint: hasSkills
      ? undefined
      : "Añade una sección de Habilidades con las tecnologías/keywords de la oferta — el matching de la mayoría de ATS es por palabras clave.",
  });
  catOf["skills"] = "estructura";

  push({
    id: "headingCount",
    label: "Número de secciones",
    weight: 0,
    status: "info",
    detail: `${headings.length} encabezado(s): ${headings.join(" · ") || "—"}`,
  });
  catOf["headingCount"] = "estructura";

  // ── Contenido ──
  push({
    id: "length",
    label: "Extensión",
    weight: 2,
    status: wordCount < 180 ? "warn" : wordCount > 1100 ? "warn" : "pass",
    detail: `${wordCount} palabras (~${Math.max(1, Math.round(wordCount / 550))} pág.).`,
    hint:
      wordCount < 180
        ? "Parece corto: desarrolla logros y responsabilidades con detalle."
        : wordCount > 1100
          ? "Parece largo (>2 páginas): recorta a lo más relevante para la oferta."
          : undefined,
  });
  catOf["length"] = "contenido";

  // Bullets: lines starting with - or + at line start.
  const bulletLines = src
    .split("\n")
    .map((l) => l.match(/^\s*[-+]\s+(.*)$/))
    .filter(Boolean)
    .map((m) => (m as RegExpMatchArray)[1]);
  const bulletsWithVerb = bulletLines.filter((b) => {
    const first = normalize(b.replace(/[*_`#\[\]]/g, "").trim().split(/\s+/)[0] ?? "");
    return ACTION_VERBS.has(first);
  }).length;
  const verbRatio = bulletLines.length ? bulletsWithVerb / bulletLines.length : 0;
  push({
    id: "actionVerbs",
    label: "Viñetas con verbos de acción",
    weight: 1,
    status:
      bulletLines.length === 0 ? "warn" : verbRatio >= 0.5 ? "pass" : verbRatio > 0 ? "warn" : "warn",
    detail:
      bulletLines.length === 0
        ? "No se detectaron viñetas."
        : `${bulletsWithVerb}/${bulletLines.length} viñetas empiezan por verbo de acción.`,
    hint:
      bulletLines.length === 0
        ? "Describe tu experiencia con viñetas (- ) que empiecen por verbo: «Lideré…», «Automaticé…»."
        : verbRatio >= 0.5
          ? undefined
          : "Empieza cada viñeta con un verbo de acción en pasado (Lideré, Desarrollé, Reduje…).",
  });
  catOf["actionVerbs"] = "contenido";

  // Quantified achievements: bullets containing numbers/%/currency.
  const quantified = bulletLines.filter((b) => /\d|%|€|\$/.test(b)).length;
  push({
    id: "metrics",
    label: "Logros cuantificados",
    weight: 1,
    status: bulletLines.length === 0 ? "info" : quantified > 0 ? "pass" : "warn",
    detail:
      bulletLines.length === 0
        ? "Sin viñetas que medir."
        : `${quantified} viñeta(s) con cifras/porcentajes.`,
    hint:
      quantified > 0 || bulletLines.length === 0
        ? undefined
        : "Cuantifica impacto: «reduje el tiempo un 40%» pesa más que «responsable de X».",
  });
  catOf["metrics"] = "contenido";

  // ── Formato / riesgos ATS ──
  const multiColumn = /#columns?\s*\(/.test(src);
  push({
    id: "columns",
    label: "Diseño a una columna",
    weight: 3,
    status: multiColumn ? "fail" : "pass",
    detail: multiColumn
      ? "Se detectó #columns(...) — diseño multi-columna."
      : "Sin multi-columna de página.",
    hint: multiColumn
      ? "Evita #columns() para el layout del CV: muchos ATS mezclan el orden de lectura entre columnas."
      : undefined,
  });
  catOf["columns"] = "formato";

  const usesTable = /#table\s*\(/.test(src);
  const usesLayoutGrid = /#grid\s*\(\s*columns\s*:/.test(src);
  push({
    id: "tables",
    label: "Tablas / rejillas de maquetación",
    weight: 1,
    status: usesTable ? "warn" : usesLayoutGrid ? "info" : "pass",
    detail: usesTable
      ? "Se detectó #table(...)."
      : usesLayoutGrid
        ? "Se usa #grid(columns: …) — habitual para puesto/fecha."
        : "Sin tablas ni rejillas.",
    hint: usesTable
      ? "Evita #table para contenido: algunos ATS lo aplanan mal. Usa texto o #grid solo para puesto+fecha."
      : undefined,
  });
  catOf["tables"] = "formato";

  // Images other than the profile photo.
  const imageUrls = [...src.matchAll(/image\s*\(\s*"([^"]*)"/g)].map((m) => m[1]);
  const nonPhotoImages = imageUrls.filter((u) => !/profile\.png$/i.test(u));
  push({
    id: "images",
    label: "Texto dentro de imágenes",
    weight: 1,
    status: nonPhotoImages.length > 0 ? "warn" : "pass",
    detail:
      imageUrls.length === 0
        ? "Sin imágenes."
        : nonPhotoImages.length > 0
          ? `${nonPhotoImages.length} imagen(es) además de la foto.`
          : "Solo la foto de perfil.",
    hint:
      nonPhotoImages.length > 0
        ? "Los ATS no leen texto en imágenes. Asegúrate de que datos clave (contacto, skills) estén como texto."
        : undefined,
  });
  catOf["images"] = "formato";

  // Contact info trapped in page header/footer (many ATS drop those).
  const headerFooter = src.match(/#set\s+page\(([\s\S]*?)\)/g)?.join("\n") ?? "";
  const contactInHeader = /(header|footer)\s*:/.test(headerFooter) && /@|link\(|mailto/.test(headerFooter);
  push({
    id: "headerFooter",
    label: "Contacto fuera de cabecera/pie",
    weight: 1,
    status: contactInHeader ? "warn" : "pass",
    detail: contactInHeader
      ? "Hay contacto en header/footer de página."
      : "El contacto va en el cuerpo del documento.",
    hint: contactInHeader
      ? "Muchos ATS descartan cabeceras/pies de página. Pon el contacto en el cuerpo, cerca del nombre."
      : undefined,
  });
  catOf["headerFooter"] = "formato";

  // Fonts.
  const fontDecls = [...src.matchAll(/font\s*:\s*"([^"]+)"/g)].map((m) => m[1].toLowerCase());
  const exotic = [...new Set(fontDecls)].filter((f) => !SAFE_FONTS.has(f));
  push({
    id: "fonts",
    label: "Tipografías estándar",
    weight: 1,
    status: exotic.length > 0 ? "warn" : "pass",
    detail:
      fontDecls.length === 0
        ? "Fuente por defecto de la plantilla (segura)."
        : exotic.length > 0
          ? `Fuente poco común: ${exotic.join(", ")}.`
          : "Fuentes estándar.",
    hint:
      exotic.length > 0
        ? "Usa fuentes estándar (Libertinus, Arial, Times, Calibri…) para que la extracción de texto no falle."
        : undefined,
  });
  catOf["fonts"] = "formato";

  // Emoji / decorative symbols the parser may mangle.
  const emojiMatches = text.match(
    /[\u{1F300}-\u{1FAFF}\u{2190}-\u{21FF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}]/gu,
  );
  push({
    id: "symbols",
    label: "Símbolos decorativos / emojis",
    weight: 1,
    status: emojiMatches && emojiMatches.length > 0 ? "warn" : "pass",
    detail:
      emojiMatches && emojiMatches.length > 0
        ? `${emojiMatches.length} símbolo(s) no estándar en el texto.`
        : "Sin emojis ni símbolos raros en el texto.",
    hint:
      emojiMatches && emojiMatches.length > 0
        ? "Quita emojis e iconos del texto: pueden romper la extracción o colarse como caracteres basura."
        : undefined,
  });
  catOf["symbols"] = "formato";

  // ── Assemble categories + score ──
  const CATS: Array<{ id: string; title: string }> = [
    { id: "contacto", title: "Contacto" },
    { id: "estructura", title: "Estructura y secciones" },
    { id: "contenido", title: "Contenido" },
    { id: "formato", title: "Formato ATS" },
  ];
  const categories: AtsCategory[] = CATS.map((c) => ({
    id: c.id,
    title: c.title,
    checks: checks.filter((ch) => catOf[ch.id] === c.id),
  }));

  let earned = 0;
  let total = 0;
  for (const ch of checks) {
    if (ch.weight === 0 || ch.status === "info") continue;
    total += ch.weight;
    if (ch.status === "pass") earned += ch.weight;
    else if (ch.status === "warn") earned += ch.weight * 0.5;
    // fail = 0
  }
  const score = total > 0 ? Math.round((earned / total) * 100) : 100;
  const grade: AtsReport["grade"] = score >= 90 ? "A" : score >= 75 ? "B" : score >= 55 ? "C" : "D";

  const counts = {
    pass: checks.filter((c) => c.status === "pass").length,
    warn: checks.filter((c) => c.status === "warn").length,
    fail: checks.filter((c) => c.status === "fail").length,
  };

  return { score, grade, categories, wordCount, counts };
}
